import type { CSSProperties } from "react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import {
  CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import {
  useActivity, useFeatureUsage, useFunnel, usePairSections,
  usePartnerDayLoop, usePayerCorrelation, usePlatformRetention, useReadingDiversity, useSecondSynastry,
  useRetention, useTopFeatures, useWebAuthFunnel,
  type AnalyticsWindow, type PlatformFilter,
} from "@/api/admin/analytics";
import type { Period } from "@/api/admin/economics";
import { AdminErrorState } from "@/components/admin/AdminErrorState";
import { KPICard } from "@/components/admin/KPICard";
import { PeriodPicker } from "@/components/admin/PeriodPicker";
import { formatNumber, formatPercent } from "@/lib/format";
import { i18n } from "@/i18n";

/**
 * DUE-173 C5 — Usage tab.
 *
 * Backend: `/api/admin/analytics/*` (DTOs in
 * `src/api/schemas/admin_analytics.py`). Reuses the Economics
 * `PeriodPicker` (drives the `period` search param) and resolves it to
 * concrete `since`/`until` ISO dates here, since these endpoints take
 * dates directly rather than a `period` token.
 */

const DEFAULT_FUNNEL_STEPS = ["onboarding:screen_view", "natal:generate", "payment:completed"];

const pageStyle: CSSProperties = {
  padding: "16px 24px 96px",
  display: "flex", flexDirection: "column", gap: 16,
};
const headerRowStyle: CSSProperties = {
  display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap",
};
const titleStyle: CSSProperties = {
  fontFamily: "var(--font-display)", fontSize: 22, color: "var(--d-fg)",
};
const subStyle: CSSProperties = {
  fontFamily: "var(--font-body)", fontSize: 12, color: "var(--d-fg-muted)",
};
const grid3: CSSProperties = {
  display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 12,
};
const splitStyle: CSSProperties = {
  display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12,
};
// A single KPI card (paused-inactive) — grid3's 3 columns would leave two
// empty cells for a lone card, so it gets its own bounded-width row.
const kpiSoloStyle: CSSProperties = {
  display: "grid", gridTemplateColumns: "minmax(0, 260px)", gap: 12,
};
const selectStyle: CSSProperties = {
  height: 32,
  padding: "0 12px",
  borderRadius: 999,
  border: "1px solid var(--d-line)",
  background: "var(--d-ink-2)",
  color: "var(--d-fg)",
  fontFamily: "var(--font-body)",
  fontSize: 13,
};
const cardStyle: CSSProperties = {
  background: "var(--d-ink-2)",
  border: "1px solid var(--d-line)",
  borderRadius: 22,
  padding: 16,
  overflowX: "auto",
};
const cardTitleStyle: CSSProperties = {
  fontFamily: "var(--font-display)", fontSize: 16, color: "var(--d-fg)", marginBottom: 10,
};
const tableStyle: CSSProperties = { width: "100%", borderCollapse: "collapse" };
const thStyle: CSSProperties = {
  fontFamily: "var(--font-body)",
  fontSize: 11,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: "var(--d-fg-dim)",
  textAlign: "right",
  padding: "8px 12px 8px 0",
  borderBottom: "1px solid var(--d-line)",
};
const tdStyle: CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 12,
  fontVariantNumeric: "tabular-nums",
  color: "var(--d-fg)",
  padding: "10px 12px 10px 0",
  borderBottom: "1px solid var(--d-line)",
  textAlign: "right",
};
const emptyStyle: CSSProperties = {
  fontFamily: "var(--font-body)", fontSize: 12, color: "var(--d-fg-dim)",
};
const chartWrapStyle: CSSProperties = {
  width: "100%",
  height: 280,
  background: "var(--d-ink-2)",
  border: "1px solid var(--d-line)",
  borderRadius: 22,
  padding: 16,
};
const tickStyle = {
  fontFamily: "var(--font-mono)",
  fontSize: 11,
  fill: "rgba(236,232,245,0.62)",
};
const funnelRowStyle: CSSProperties = {
  display: "flex", justifyContent: "space-between", alignItems: "baseline",
};
const funnelLabelStyle: CSSProperties = {
  fontFamily: "var(--font-body)", fontSize: 12, color: "var(--d-fg-muted)",
};
const funnelValueStyle: CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 14,
  fontVariantNumeric: "tabular-nums",
  color: "var(--d-fg)",
};
const nHintStyle: CSSProperties = {
  fontFamily: "var(--font-body)",
  fontSize: 11,
  color: "var(--d-fg-dim)",
};
function periodToRange(period: Period): { since: string; until: string; days: number } {
  const days = period === "last_7d" ? 7 : period === "last_90d" ? 90 : 30;
  const until = new Date();
  const since = new Date(until);
  since.setUTCDate(since.getUTCDate() - (days - 1));
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  return { since: iso(since), until: iso(until), days };
}

// The partner-day-loop cohort-admission window must NOT follow the page's
// period picker (7d/30d/90d): a 14-day loop plus D7 needs members >= 21
// days old, and under a short admission window nobody could ever mature.
// Fixed at 90 days, independent of `period`.
const COHORT_LOOKBACK_DAYS = 90;

function cohortWindow(): { since: string; until: string } {
  const until = new Date();
  const since = new Date(until);
  since.setUTCDate(since.getUTCDate() - (COHORT_LOOKBACK_DAYS - 1));
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  return { since: iso(since), until: iso(until) };
}

// Transit diversity spec §6: the success criterion is a 7-day rate, so the
// panel keeps its own fixed window regardless of the page's period filter.
const READING_DIVERSITY_DAYS = 7;

function readingDiversityWindow(): { since: string; until: string } {
  const until = new Date();
  const since = new Date(until);
  since.setUTCDate(since.getUTCDate() - (READING_DIVERSITY_DAYS - 1));
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  return { since: iso(since), until: iso(until) };
}

// DUE-412: the second-synastry target is read over a fixed 28-day window.
const SECOND_SYNASTRY_DAYS = 28;

function secondSynastryWindow(): { since: string; until: string } {
  const until = new Date();
  const since = new Date(until);
  since.setUTCDate(since.getUTCDate() - (SECOND_SYNASTRY_DAYS - 1));
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  return { since: iso(since), until: iso(until) };
}

// DUE-336: the per-method columns of the keep-access funnel, in funnel order.
const WEB_AUTH_METHOD_COLS = [
  "started", "completed", "failed", "linked", "merged", "signed_in", "replaced",
] as const;

type PlatformOption = "all" | PlatformFilter;

type PlatformLabelKey =
  | "usage.platform_all"
  | "usage.platform_telegram"
  | "usage.platform_ios"
  | "usage.platform_web";

const PLATFORM_OPTIONS: Array<{ value: PlatformOption; labelKey: PlatformLabelKey }> = [
  { value: "all", labelKey: "usage.platform_all" },
  { value: "telegram", labelKey: "usage.platform_telegram" },
  { value: "ios", labelKey: "usage.platform_ios" },
  { value: "web", labelKey: "usage.platform_web" },
];

export function UsagePage(): JSX.Element {
  const { t } = useTranslation("admin");
  const [params] = useSearchParams();
  const period = (params.get("period") as Period) ?? "last_30d";
  const lng = i18n.language || "en";
  const [platform, setPlatform] = useState<PlatformOption>("all");

  const { since, until, days: periodDays } = useMemo(() => periodToRange(period), [period]);
  const win: AnalyticsWindow = useMemo(
    () => ({ since, until, platform: platform === "all" ? undefined : platform }),
    [since, until, platform],
  );

  const activityQuery = useActivity(win);
  const topFeaturesQuery = useTopFeatures(win);
  const featureUsageQuery = useFeatureUsage(win);
  const retentionQuery = useRetention(win);
  const payersQuery = usePayerCorrelation(win);
  // DUE-336: onboarding → first chart has to be comparable web vs Telegram,
  // so the funnel follows the platform selector like the panels above it.
  const funnelQuery = useFunnel(DEFAULT_FUNNEL_STEPS, win);
  const loopWindow = useMemo(() => cohortWindow(), []);
  const sectionsQuery = usePairSections({ since, until });
  // DUE-293: fixed 90-day admission window — see `cohortWindow`.
  const partnerDayLoopQuery = usePartnerDayLoop(loopWindow);
  const sections = sectionsQuery.data ?? { sections: [], locked: [] };
  const loop = partnerDayLoopQuery.data;
  // DUE-336: D1/D7 by starting platform, on the fixed cohort window —
  // a 30-day period would drop everyone who has not had a D7 yet.
  const platformRetentionQuery = usePlatformRetention(loopWindow);
  const platformRetention = platformRetentionQuery.data ?? [];
  // DUE-336: the keep-access prompt is a web screen; iOS Apple sign-in runs
  // through the same service and must not dilute its funnel.
  const webAuthQuery = useWebAuthFunnel({ since, until, platform: "web" });
  const webAuth = webAuthQuery.data;
  const pctOrNotYet = (pct: number | null): string =>
    pct === null ? t("usage.habit_retention_not_matured") : formatPercent(pct, lng, 1);
  // An empty denominator on the web-auth panel is "nothing to divide", not an
  // immature cohort — a dash, like the retention grid's missing cells.
  const pctOrDash = (pct: number | null): string =>
    pct === null ? "—" : formatPercent(pct, lng, 1);
  const diversityWindow = useMemo(() => readingDiversityWindow(), []);
  const diversityQuery = useReadingDiversity(diversityWindow);
  const diversity = diversityQuery.data;
  const secondSynastryWin = useMemo(() => secondSynastryWindow(), []);
  const secondSynastryQuery = useSecondSynastry(secondSynastryWin);
  const secondSynastry = secondSynastryQuery.data;

  // DUE-309: the share of up-votes on a relation's days. No vote yet is not
  // 0% — it is the same "hasn't matured" the retention columns show.
  const upRate = (up: number, votes: number): string =>
    votes === 0
      ? t("usage.habit_retention_not_matured")
      : formatPercent((up / votes) * 100, lng, 0);

  const activity = activityQuery.data;
  const topFeatures = topFeaturesQuery.data ?? [];
  const featureUsage = featureUsageQuery.data ?? [];
  const retention = retentionQuery.data ?? [];
  const payers = payersQuery.data ?? [];
  const funnel = funnelQuery.data ?? [];

  const days = activity?.days ?? [];
  // Ascending order guaranteed by AnalyticsDashboardService.activity() —
  // the most recent day is the last entry.
  const dau = days.length > 0 ? days[days.length - 1].dau : 0;

  const trendData = useMemo(() => {
    const byDay = new Map<string, number>();
    for (const r of featureUsage) {
      byDay.set(r.day, (byDay.get(r.day) ?? 0) + r.events_count);
    }
    return [...byDay.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([day, events_count]) => ({ day, events_count }));
  }, [featureUsage]);

  const maxWeek = retention.reduce(
    (max, c) => c.weeks.reduce((m, w) => Math.max(m, w.week_index), max),
    0,
  );
  const weekIndices = Array.from({ length: maxWeek + 1 }, (_, i) => i);

  // DUE-216 — `push.paused_inactive` is one row per (user, day) held back,
  // already rolled up by the generic feature-usage endpoint: no dedicated
  // backend query needed, `events_count` summed IS the avoided-generation
  // count.
  const pausedInactiveTotal = featureUsage
    .filter((r) => r.feature === "push" && r.action === "paused_inactive")
    .reduce((sum, r) => sum + r.events_count, 0);
  const pausedInactiveAvgPerDay = periodDays > 0
    ? Math.round((pausedInactiveTotal / periodDays) * 10) / 10
    : 0;

  return (
    <div style={pageStyle}>
      <div style={headerRowStyle}>
        <div>
          <div style={titleStyle}>{t("usage.title")}</div>
          <div style={subStyle}>{t("usage.subtitle")}</div>
        </div>
        <select
          data-testid="platform-select"
          style={selectStyle}
          value={platform}
          onChange={(e) => setPlatform(e.target.value as PlatformOption)}
        >
          {PLATFORM_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
          ))}
        </select>
      </div>

      <PeriodPicker />

      <div style={grid3}>
        {activityQuery.isError ? (
          <AdminErrorState onRetry={() => void activityQuery.refetch()} />
        ) : (
          <>
            <div data-testid="kpi-dau">
              <KPICard label={t("usage.kpi.dau")} value={formatNumber(dau, lng)} />
            </div>
            <div data-testid="kpi-wau">
              <KPICard label={t("usage.kpi.wau")} value={formatNumber(activity?.wau ?? 0, lng)} />
            </div>
            <div data-testid="kpi-mau">
              <KPICard label={t("usage.kpi.mau")} value={formatNumber(activity?.mau ?? 0, lng)} />
            </div>
          </>
        )}
      </div>

      <div style={cardStyle} data-testid="top-features-table">
        <div style={cardTitleStyle}>{t("usage.top_features")}</div>
        {topFeaturesQuery.isError ? (
          <AdminErrorState onRetry={() => void topFeaturesQuery.refetch()} />
        ) : topFeatures.length === 0 ? (
          <div style={emptyStyle}>{t("usage.no_data")}</div>
        ) : (
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={{ ...thStyle, textAlign: "left" }}>{t("usage.top_features_col.feature")}</th>
                <th style={thStyle}>{t("usage.top_features_col.events")}</th>
                <th style={thStyle}>{t("usage.top_features_col.unique_users")}</th>
              </tr>
            </thead>
            <tbody>
              {topFeatures.map((r) => (
                <tr key={r.feature}>
                  <td style={{ ...tdStyle, textAlign: "left" }}>{r.feature}</td>
                  <td style={tdStyle}>{formatNumber(r.events_count, lng)}</td>
                  <td style={tdStyle}>{formatNumber(r.unique_users, lng)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div data-testid="usage-trend-chart" style={chartWrapStyle}>
        <div style={cardTitleStyle}>{t("usage.trend")}</div>
        {featureUsageQuery.isError ? (
          <AdminErrorState onRetry={() => void featureUsageQuery.refetch()} />
        ) : (
          <ResponsiveContainer width="100%" height="80%">
            <ComposedChart data={trendData}>
              <CartesianGrid stroke="rgba(230,220,255,0.08)" vertical={false} />
              <XAxis dataKey="day" tick={tickStyle} tickLine={false} axisLine={false} />
              <YAxis tick={tickStyle} tickLine={false} axisLine={false} width={48} />
              <Tooltip contentStyle={{
                background: "var(--d-ink-3)",
                border: "1px solid var(--d-line-2)",
                borderRadius: 12,
                fontFamily: "var(--font-mono)",
                fontSize: 12,
                color: "var(--d-fg)",
              }} />
              <Line type="monotone" dataKey="events_count" name={t("usage.trend")}
                    stroke="var(--d-violet-soft)" strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>

      <div style={splitStyle}>
        <div style={cardStyle} data-testid="usage-funnel">
          <div style={cardTitleStyle}>{t("usage.funnel")}</div>
          {funnelQuery.isError ? (
            <AdminErrorState onRetry={() => void funnelQuery.refetch()} />
          ) : funnel.length === 0 ? (
            <div style={emptyStyle}>{t("usage.no_data")}</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {funnel.map((step, idx) => (
                <div key={`${step.feature}:${step.action}`} style={funnelRowStyle}>
                  <span style={funnelLabelStyle}>{idx + 1}. {step.feature}:{step.action}</span>
                  <span style={funnelValueStyle}>
                    {formatNumber(step.users, lng)} · {formatPercent(step.conversion_pct, lng, 1)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={cardStyle} data-testid="usage-payers-table">
          <div style={cardTitleStyle}>{t("usage.payers")}</div>
          {payersQuery.isError ? (
            <AdminErrorState onRetry={() => void payersQuery.refetch()} />
          ) : payers.length === 0 ? (
            <div style={emptyStyle}>{t("usage.no_data")}</div>
          ) : (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={{ ...thStyle, textAlign: "left" }}>{t("usage.payers_col.feature")}</th>
                  <th style={thStyle}>{t("usage.payers_col.payers")}</th>
                  <th style={thStyle}>{t("usage.payers_col.non_payers")}</th>
                </tr>
              </thead>
              <tbody>
                {payers.map((r) => (
                  <tr key={r.feature}>
                    <td style={{ ...tdStyle, textAlign: "left" }}>{r.feature}</td>
                    <td style={tdStyle}>{formatNumber(r.payer_users, lng)}</td>
                    <td style={tdStyle}>{formatNumber(r.non_payer_users, lng)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div style={cardStyle} data-testid="usage-retention-table">
        <div style={cardTitleStyle}>{t("usage.retention")}</div>
        {retentionQuery.isError ? (
          <AdminErrorState onRetry={() => void retentionQuery.refetch()} />
        ) : retention.length === 0 ? (
          <div style={emptyStyle}>{t("usage.no_data")}</div>
        ) : (
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={{ ...thStyle, textAlign: "left" }}>{t("usage.retention_col.cohort")}</th>
                <th style={thStyle}>{t("usage.retention_col.size")}</th>
                {weekIndices.map((w) => (
                  <th key={w} style={thStyle}>{t("usage.retention_col.week", { n: w })}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {retention.map((c) => (
                <tr key={c.cohort_week}>
                  <td style={{ ...tdStyle, textAlign: "left" }}>{c.cohort_week}</td>
                  <td style={tdStyle}>{formatNumber(c.cohort_size, lng)}</td>
                  {weekIndices.map((w) => {
                    const cell = c.weeks.find((wk) => wk.week_index === w);
                    return (
                      <td key={w} style={tdStyle}>
                        {cell ? formatPercent(cell.retention_pct, lng, 0) : "—"}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div style={cardStyle} data-testid="platform-retention-table">
        <div style={cardTitleStyle}>{t("usage.platform_retention")}</div>
        <div style={subStyle}>
          {t("usage.habit_retention_window_note", { n: COHORT_LOOKBACK_DAYS })}{" "}
          {t("usage.platform_retention_note")}
        </div>
        {platformRetentionQuery.isError ? (
          <AdminErrorState onRetry={() => void platformRetentionQuery.refetch()} />
        ) : (
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={{ ...thStyle, textAlign: "left" }}>{t("usage.platform_retention_col.platform")}</th>
                <th style={thStyle}>{t("usage.habit_retention_col.size")}</th>
                <th style={thStyle}>{t("usage.platform_retention_col.d1")}</th>
                <th style={thStyle}>{t("usage.habit_retention_col.d7")}</th>
              </tr>
            </thead>
            <tbody>
              {platformRetention.map((row) => (
                <tr key={row.platform} data-testid={`platform-retention-${row.platform}`}>
                  <td style={{ ...tdStyle, textAlign: "left" }}>
                    {t(`usage.platform_${row.platform}`)}
                  </td>
                  <td style={tdStyle}>{formatNumber(row.cohort_size, lng)}</td>
                  {([
                    [row.d1_retention_pct, row.d1_eligible],
                    [row.d7_retention_pct, row.d7_eligible],
                  ] as const).map(([pct, eligible], i) => (
                    <td key={i} style={tdStyle}>
                      {pctOrNotYet(pct)}
                      {pct !== null && (
                        <>
                          {" "}
                          <span style={nHintStyle}>
                            ({t("usage.habit_retention_n", { n: formatNumber(eligible, lng) })})
                          </span>
                        </>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div style={cardStyle} data-testid="web-auth-funnel">
        <div style={cardTitleStyle}>{t("usage.web_auth")}</div>
        <div style={subStyle}>
          {t("usage.web_auth_note")} {t("usage.raw_events_note")}
        </div>
        {webAuthQuery.isError ? (
          <AdminErrorState onRetry={() => void webAuthQuery.refetch()} />
        ) : webAuth && (
          <>
            <div style={funnelRowStyle}>
              <span style={funnelLabelStyle}>{t("usage.web_auth_row.success")}</span>
              <span style={funnelValueStyle} data-testid="web-auth-success">
                {pctOrDash(webAuth.success_pct)}{" "}
                <span style={nHintStyle}>
                  ({formatNumber(webAuth.kept, lng)} / {formatNumber(webAuth.prompt_shown_users, lng)})
                </span>
              </span>
            </div>
            <div style={funnelRowStyle}>
              <span style={funnelLabelStyle}>{t("usage.web_auth_row.merged_share")}</span>
              <span style={funnelValueStyle} data-testid="web-auth-merged-share">
                {pctOrDash(webAuth.merged_share_pct)}{" "}
                <span style={nHintStyle}>
                  ({formatNumber(webAuth.merged, lng)} / {formatNumber(webAuth.kept, lng)})
                </span>
              </span>
            </div>
            {([
              ["prompt_shown", webAuth.prompt_shown],
              ["started", webAuth.started],
              ["completed", webAuth.completed],
              ["failed", webAuth.failed],
            ] as const).map(([key, value]) => (
              <div style={funnelRowStyle} key={key}>
                <span style={funnelLabelStyle}>{t(`usage.web_auth_row.${key}`)}</span>
                <span style={funnelValueStyle}>{formatNumber(value, lng)}</span>
              </div>
            ))}
            {webAuth.by_method.length === 0 ? (
              <div style={emptyStyle}>{t("usage.no_data")}</div>
            ) : (
              <table style={{ ...tableStyle, marginTop: 12 }}>
                <thead>
                  <tr>
                    <th style={{ ...thStyle, textAlign: "left" }}>{t("usage.web_auth_col.method")}</th>
                    {WEB_AUTH_METHOD_COLS.map((col) => (
                      <th key={col} style={thStyle}>{t(`usage.web_auth_col.${col}`)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {webAuth.by_method.map((row) => (
                    <tr key={row.method} data-testid={`web-auth-method-${row.method}`}>
                      <td style={{ ...tdStyle, textAlign: "left" }}>{row.method}</td>
                      {WEB_AUTH_METHOD_COLS.map((col) => (
                        <td key={col} style={tdStyle}>{formatNumber(row[col], lng)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>

      <div style={cardStyle} data-testid="pair-sections-table">
        <div style={cardTitleStyle}>{t("usage.pair_sections")}</div>
        <div style={subStyle}>{t("usage.raw_events_note")}</div>
        {sectionsQuery.isError ? (
          <AdminErrorState onRetry={() => void sectionsQuery.refetch()} />
        ) : sections.sections.length === 0 && sections.locked.length === 0 ? (
          <div style={emptyStyle}>{t("usage.pair_sections_empty")}</div>
        ) : (
          <>
            {sections.sections.length === 0 ? (
              <div style={emptyStyle} data-testid="pair-sections-empty">{t("usage.no_data")}</div>
            ) : (
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={{ ...thStyle, textAlign: "left" }}>{t("usage.pair_sections_col.reading")}</th>
                    <th style={{ ...thStyle, textAlign: "left" }}>{t("usage.pair_sections_col.section")}</th>
                    <th style={{ ...thStyle, textAlign: "left" }}>{t("usage.pair_sections_col.half")}</th>
                    <th style={thStyle}>{t("usage.pair_sections_col.events")}</th>
                    <th style={thStyle}>{t("usage.pair_sections_col.users")}</th>
                  </tr>
                </thead>
                <tbody>
                  {sections.sections.map((row) => (
                    <tr
                      key={`${row.feature}-${row.section}-${row.lens}`}
                      data-testid={`section-row-${row.feature}-${row.section}-${row.lens ? "lens" : "general"}`}
                    >
                      <td style={{ ...tdStyle, textAlign: "left" }}>{row.feature}</td>
                      <td style={{ ...tdStyle, textAlign: "left" }}>{row.section}</td>
                      <td style={{ ...tdStyle, textAlign: "left" }}>
                        {t(row.lens ? "usage.pair_sections_half.lens" : "usage.pair_sections_half.general")}
                      </td>
                      <td style={tdStyle}>{formatNumber(row.events, lng)}</td>
                      <td style={tdStyle}>{formatNumber(row.unique_users, lng)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div style={{ ...cardTitleStyle, marginTop: 16 }}>{t("usage.locked_taps")}</div>
            {sections.locked.length === 0 ? (
              <div style={emptyStyle} data-testid="locked-taps-empty">{t("usage.no_data")}</div>
            ) : (
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={{ ...thStyle, textAlign: "left" }}>{t("usage.locked_taps_col.surface")}</th>
                    <th style={thStyle}>{t("usage.locked_taps_col.events")}</th>
                    <th style={thStyle}>{t("usage.pair_sections_col.users")}</th>
                  </tr>
                </thead>
                <tbody>
                  {sections.locked.map((row) => (
                    <tr key={row.surface} data-testid={`locked-row-${row.surface}`}>
                      <td style={{ ...tdStyle, textAlign: "left" }}>{row.surface}</td>
                      <td style={tdStyle}>{formatNumber(row.events, lng)}</td>
                      <td style={tdStyle}>{formatNumber(row.unique_users, lng)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>

      <div style={cardStyle} data-testid="partner-day-loop-table">
        <div style={cardTitleStyle}>{t("usage.partner_day_loop")}</div>
        <div style={subStyle}>
          {t("usage.habit_retention_window_note", { n: COHORT_LOOKBACK_DAYS })}{" "}
          {t("usage.raw_events_note")}
        </div>
        {partnerDayLoopQuery.isError ? (
          <AdminErrorState onRetry={() => void partnerDayLoopQuery.refetch()} />
        ) : loop && (
          <>
            <div style={funnelRowStyle}>
              <span style={funnelLabelStyle}>{t("usage.partner_day_loop_share")}</span>
              <span style={funnelValueStyle} data-testid="loop-share">
                {loop.loop_share_pct === null
                  ? t("usage.habit_retention_not_matured")
                  : formatPercent(loop.loop_share_pct, lng, 1)}{" "}
                <span style={nHintStyle}>
                  ({t("usage.partner_day_loop_n", {
                    users: formatNumber(loop.loop_users, lng),
                    eligible: formatNumber(loop.loop_eligible, lng),
                    cohort: formatNumber(loop.cohort_size, lng),
                  })})
                </span>
              </span>
            </div>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={{ ...thStyle, textAlign: "left" }}>{t("usage.habit_retention_col.cohort")}</th>
                  <th style={thStyle}>{t("usage.habit_retention_col.size")}</th>
                  <th style={thStyle}>{t("usage.habit_retention_col.d7")}</th>
                </tr>
              </thead>
              <tbody>
                {loop.groups.map((g) => (
                  <tr key={g.group}>
                    <td style={{ ...tdStyle, textAlign: "left" }}>
                      {t(g.group === "day_viewers"
                        ? "usage.partner_day_loop_group.day_viewers"
                        : "usage.partner_day_loop_group.never_viewed")}
                    </td>
                    <td style={tdStyle}>{formatNumber(g.cohort_size, lng)}</td>
                    <td style={tdStyle}>
                      {g.d7_retention_pct === null ? (
                        <span>{t("usage.habit_retention_not_matured")}</span>
                      ) : (
                        <>
                          <span>{formatPercent(g.d7_retention_pct, lng, 1)}</span>{" "}
                          <span style={nHintStyle}>
                            ({t("usage.habit_retention_n", { n: formatNumber(g.d7_eligible, lng) })})
                          </span>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ ...cardTitleStyle, marginTop: 16 }}>{t("usage.lens_funnel")}</div>
            {([
              ["locked_synastry", loop.lens.locked_tapped_synastry_users],
              ["locked_day", loop.lens.locked_tapped_day_users],
              ["relation_paid_sets", loop.lens.relation_paid_sets],
              ["purchasers", loop.lens.purchasers],
              ["reading_viewers", loop.lens.lens_reading_viewers],
              ["day_viewers", loop.lens.lens_day_viewers],
            ] as const).map(([key, value]) => (
              <div style={funnelRowStyle} key={key}>
                <span style={funnelLabelStyle}>{t(`usage.lens_funnel_row.${key}`)}</span>
                <span style={funnelValueStyle} data-testid={`lens-${key.replace(/_/g, "-")}`}>
                  {formatNumber(value, lng)}
                </span>
              </div>
            ))}
            <div style={funnelRowStyle}>
              <span style={funnelLabelStyle}>{t("usage.lens_funnel_row.labelled_users")}</span>
              <span style={funnelValueStyle} data-testid="lens-labelled-users">
                {formatNumber(loop.lens.labelled_users, lng)}
              </span>
            </div>
            {([
              ["reading_viewed_pct", loop.lens.lens_reading_viewed_pct,
                loop.lens.lens_reading_viewers, loop.lens.labelled_users],
              ["day_viewed_pct", loop.lens.lens_day_viewed_pct,
                loop.lens.lens_day_viewers, loop.lens.labelled_users],
              ["paid_per_locked_pct", loop.lens.paid_per_locked_pct,
                loop.lens.relation_paid_set_users, loop.lens.locked_tapped_users],
              ["purchasers_per_locked_pct", loop.lens.purchasers_per_locked_pct,
                loop.lens.purchasers, loop.lens.locked_tapped_users],
            ] as const).map(([key, pct, n, of]) => (
              <div style={funnelRowStyle} key={key}>
                <span style={funnelLabelStyle}>{t(`usage.lens_funnel_row.${key}`)}</span>
                <span style={funnelValueStyle} data-testid={`lens-${key.replace(/_/g, "-")}`}>
                  {pctOrNotYet(pct)}{" "}
                  <span style={nHintStyle}>
                    ({formatNumber(n, lng)} / {formatNumber(of, lng)})
                  </span>
                </span>
              </div>
            ))}
            <div style={{ ...cardTitleStyle, marginTop: 16 }}>{t("usage.lens_by_relation.title")}</div>
            <table style={tableStyle} data-testid="lens-by-relation">
              <thead>
                <tr>
                  <th style={{ ...thStyle, textAlign: "left" }}>{t("usage.lens_by_relation.relation")}</th>
                  <th style={thStyle}>{t("usage.lens_by_relation.plain")}</th>
                  <th style={thStyle}>{t("usage.lens_by_relation.lens")}</th>
                  <th style={thStyle}>{t("usage.lens_by_relation.votes")}</th>
                </tr>
              </thead>
              <tbody>
                {loop.lens.by_relation.map((r) => (
                  <tr key={r.relation_type} data-testid={`lens-relation-${r.relation_type}`}>
                    <td style={{ ...tdStyle, textAlign: "left" }}>
                      {t(`usage.lens_by_relation.type.${r.relation_type}`, {
                        defaultValue: r.relation_type,
                      })}
                    </td>
                    <td style={tdStyle}>{upRate(r.plain_up, r.plain_votes)}</td>
                    <td style={tdStyle}>{upRate(r.lens_up, r.lens_votes)}</td>
                    <td style={tdStyle}>{formatNumber(r.plain_votes + r.lens_votes, lng)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>

      <div style={cardStyle} data-testid="reading-diversity-table">
        <div style={cardTitleStyle}>{t("usage.reading_diversity")}</div>
        <div style={subStyle}>
          {t("usage.reading_diversity_note")} {t("usage.raw_events_note")}
        </div>
        {diversityQuery.isError ? (
          <AdminErrorState onRetry={() => void diversityQuery.refetch()} />
        ) : diversity && (
          ([
            ["transit_opener", diversity.transit_opener_repeat_pct,
              diversity.transit_opener_repeats, diversity.transit_days],
            ["transit_lead", diversity.transit_lead_repeat_pct,
              diversity.transit_lead_repeats, diversity.transit_days],
            ["partner_opener", diversity.partner_opener_repeat_pct,
              diversity.partner_opener_repeats, diversity.partner_days],
            ["partner_lists", diversity.partner_lists_repeat_pct,
              diversity.partner_lists_repeats, diversity.partner_lists_days],
            ["partner_angle", diversity.partner_angle_repeat_pct,
              diversity.partner_angle_repeats, diversity.partner_angle_days],
            ["depth_per_push", diversity.depth_per_push_pct,
              diversity.depth_viewed, diversity.push_sent],
          ] as const).map(([key, pct, n, of]) => (
            <div style={funnelRowStyle} key={key}>
              <span style={funnelLabelStyle}>{t(`usage.reading_diversity_row.${key}`)}</span>
              <span style={funnelValueStyle} data-testid={`diversity-${key.replace(/_/g, "-")}`}>
                {pct === null
                  ? t("usage.habit_retention_not_matured")
                  : formatPercent(pct, lng, 1)}{" "}
                <span style={nHintStyle}>
                  ({formatNumber(n, lng)} / {formatNumber(of, lng)})
                </span>
              </span>
            </div>
          ))
        )}
      </div>

      <div style={cardStyle} data-testid="second-synastry-card">
        <div style={cardTitleStyle}>{t("usage.second_synastry")}</div>
        <div style={subStyle}>
          {t("usage.second_synastry_note", { n: SECOND_SYNASTRY_DAYS })}
        </div>
        {secondSynastryQuery.isError ? (
          <AdminErrorState onRetry={() => void secondSynastryQuery.refetch()} />
        ) : secondSynastry && (
          <div style={funnelRowStyle}>
            <span style={funnelValueStyle} data-testid="second-synastry-rate">
              {pctOrDash(secondSynastry.rate_pct)}
            </span>
            <span style={funnelValueStyle} data-testid="second-synastry-users">
              <span style={nHintStyle}>
                ({formatNumber(secondSynastry.second_synastry_users, lng)} /{" "}
                {formatNumber(secondSynastry.synastry_users, lng)})
              </span>
            </span>
          </div>
        )}
      </div>

      <div style={kpiSoloStyle}>
        <div data-testid="kpi-paused-inactive">
          {featureUsageQuery.isError ? (
            <AdminErrorState onRetry={() => void featureUsageQuery.refetch()} />
          ) : (
            <KPICard
              label={t("usage.kpi.paused_inactive")}
              value={formatNumber(pausedInactiveTotal, lng)}
              hint={t("usage.kpi.paused_inactive_hint", {
                avg: formatNumber(pausedInactiveAvgPerDay, lng),
              })}
            />
          )}
        </div>
      </div>
    </div>
  );
}
